import React, { useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShoppingBag, FileText, Eye, Minus, Plus, Star } from "lucide-react";
import StockBadge from "./StockBadge";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProductCard({ product: p, index = 0, onQuickView, onRequestQuote }) {
    const { add } = useCart();
    const { isB2B, isAdmin } = useAuth();
    const [qty, setQty] = useState(1);
    const isWholesale = isB2B || isAdmin;
    const soldOut = p.stock_status === "out_of_stock";
    const price = isWholesale && p.wholesale_price ? p.wholesale_price : p.price;

    return (
        <div data-testid={`product-card-${index}`} className="group bg-charcoal-800 border border-[#E2D4C1] hover:border-gold/50 transition-all duration-500 flex flex-col">
            <div className="aspect-[4/3] overflow-hidden bg-[#E2D4C1] relative">
                {p.image && <img src={p.image} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                    <StockBadge status={p.stock_status} testId={`stock-badge-${index}`} />
                    {p.mbg_recommended && (
                        <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase px-2 py-1 border border-gold/40 text-gold bg-[#8C5A3C]/10">
                            <Star size={9} className="fill-gold" /> MBG Pick
                        </span>
                    )}
                </div>
                <button
                    data-testid={`quick-view-${index}`}
                    onClick={() => onQuickView?.(p)}
                    className="absolute bottom-3 right-3 w-9 h-9 grid place-items-center bg-[#F7F3EB]/90 backdrop-blur border border-[#E2D4C1] text-[#33302C] hover:text-gold hover:border-gold/40 opacity-0 group-hover:opacity-100 transition-all"
                    title="Quick view"
                >
                    <Eye size={15} />
                </button>
            </div>
            <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[#6B645C]">{p.brand || p.product_type || "MBG"}</span>
                    {p.mbg_sku && <span className="text-[9px] font-mono text-[#6B645C]">{p.mbg_sku}</span>}
                </div>
                <div className="font-display text-lg leading-tight mb-1">{p.name}</div>
                <p className="text-[#6B645C] text-xs mb-3 line-clamp-2 flex-1">{p.description}</p>

                {(p.vlt_options?.length || p.roll_sizes?.length) ? (
                    <div className="flex flex-wrap gap-1 mb-3">
                        {(p.vlt_options || []).slice(0, 4).map((v) => (
                            <span key={v} className="text-[9px] px-1.5 py-0.5 border border-[#E2D4C1] text-[#33302C]">{v}</span>
                        ))}
                    </div>
                ) : null}

                <div className="flex items-end justify-between mb-4">
                    <div>
                        <div className="text-gold text-xl font-medium">{p.price_range || fmt(price)}</div>
                        {isWholesale && p.wholesale_price && (
                            <div className="text-[10px] tracking-[0.15em] uppercase text-gold-light mt-0.5" data-testid={`wholesale-price-${index}`}>
                                Wholesale {fmt(p.wholesale_price)}
                            </div>
                        )}
                    </div>
                    {p.supplier_sku && isAdmin && <span className="text-[9px] font-mono text-[#6B645C]">Sup: {p.supplier_sku}</span>}
                </div>

                <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center border border-[#E2D4C1]">
                        <button data-testid={`qty-dec-${index}`} onClick={() => setQty(Math.max(1, qty - 1))} className="w-8 h-9 grid place-items-center text-[#33302C] hover:text-gold"><Minus size={12} /></button>
                        <span data-testid={`qty-val-${index}`} className="w-8 text-center text-sm">{qty}</span>
                        <button data-testid={`qty-inc-${index}`} onClick={() => setQty(qty + 1)} className="w-8 h-9 grid place-items-center text-[#33302C] hover:text-gold"><Plus size={12} /></button>
                    </div>
                    <button
                        data-testid={`add-to-cart-${index}`}
                        disabled={soldOut}
                        onClick={() => { add({ ...p, price }, qty); toast.success(`${p.name} added to cart`); }}
                        className="flex-1 h-9 bg-gold hover:bg-gold-hover text-white uppercase tracking-[0.18em] text-[11px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <ShoppingBag size={13} /> {soldOut ? "Sold Out" : "Add"}
                    </button>
                </div>

                <button
                    data-testid={`request-quote-${index}`}
                    onClick={() => onRequestQuote?.(p)}
                    className="w-full h-9 border border-[#8C5A3C]/40 text-[#33302C] hover:border-gold hover:text-gold uppercase tracking-[0.18em] text-[11px] inline-flex items-center justify-center gap-1.5 transition-colors"
                >
                    <FileText size={13} /> Request Quote
                </button>
            </div>
        </div>
    );
}
